import { Badge, DatePicker, Icons, Input } from '@commenda/kakashi';
import { classNames, NonTimezoneSensitiveDate } from '@commenda/utils';

interface FilingInputRowProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  prefix?: React.ReactNode;
  type?: 'text' | 'number' | 'date';
  required?: boolean;
  error?: string;
  editable?: boolean;
  hint?: string;
}

export default function FilingInputRow({
  title,
  value,
  onChange,
  placeholder,
  className,
  prefix,
  type = 'text',
  required = false,
  error,
  editable = true,
  hint,
}: FilingInputRowProps) {
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;

    // Only allow digits and hyphens
    if (!/^[\d-]*$/.test(inputValue)) return;

    let formatted = inputValue;

    // Auto-insert hyphens as the user types
    if (formatted.length === 2 && !formatted.includes('-')) {
      formatted += '-';
    } else if (formatted.length === 5 && formatted.indexOf('-', 3) === -1) {
      formatted += '-';
    }

    // Limit to 10 characters maximum (DD-MM-YYYY format)
    if (formatted.length <= 10) {
      onChange(formatted);
    }
  };

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'date') handleDateChange(e);
    else onChange(e.target.value);
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '';
    const nonTzDate = NonTimezoneSensitiveDate.fromISO8601String(date);
    return nonTzDate.format('MM-DD-YYYY');
  };

  return (
    <div
      className={classNames(
        'flex divide-x divide-border-primary bg-bg-primary',
        className
      )}
    >
      <div className="py-lg px-xl txt-small-medium text-text-quaternary w-[250px] shrink-0 flex items-center gap-sm">
        <span>
          {title}
          {required && <span className="text-text-error ml-1">*</span>}
        </span>
        {hint && (
          <Badge variant="gray" size="sm">
            {hint}
          </Badge>
        )}
      </div>
      <div className="py-lg px-xl flex items-center gap-xl w-full">
        <div className="flex-1 flex items-center gap-xs">
          {prefix && (
            <span className="txt-small-medium text-text-quaternary">
              {prefix}
            </span>
          )}

          {type === 'date' ? (
            <DatePicker
              date={value}
              setDate={onChange}
              hasError={!!error}
              disabled={!editable}
              _triggerClassName="txt-small-medium text-text-secondary"
              renderTrigger={({ date, onClick }) => (
                <div className="flex items-center w-full gap-2">
                  <div className="txt-medium-regular text-text-primary">
                    {date ? formatDate(date) : 'MM-DD-YYYY'}
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick();
                    }}
                    className="cursor-pointer flex items-center justify-center bg-bg-primary size-6"
                  >
                    <Icons.CalendarPlus02 className="size-4 text-button-secondary-fg" />
                  </div>
                </div>
              )}
            />
          ) : (
            <Input
              className="!bg-bg-primary border-none shadow-none focus-visible:ring-0 h-auto p-0"
              value={value}
              onChange={onChangeValue}
              placeholder={placeholder}
              type={type}
              disabled={!editable}
            />
          )}
        </div>
        {error && (
          <div className="text-text-error txt-small-regular">{error}</div>
        )}
      </div>
    </div>
  );
}
